import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiLogIn, FiMail, FiLock } from 'react-icons/fi';
import logoImg from '../assets/logo horizontal.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-rose-900 via-red-800 to-rose-800 relative overflow-hidden">
      {/* Mathematical Background Elements - More Dense */}
      <div className="absolute inset-0 overflow-hidden opacity-15">
        <div className="absolute top-10 left-10 text-white text-9xl font-light">∑</div>
        <div className="absolute top-32 right-20 text-white text-8xl font-light">π</div>
        <div className="absolute bottom-40 left-1/4 text-white text-7xl font-light">∫</div>
        <div className="absolute top-1/2 right-16 text-white text-9xl font-light">√</div>
        <div className="absolute bottom-32 right-1/3 text-white text-8xl font-light">∞</div>
        <div className="absolute top-64 left-1/3 text-white text-7xl font-light">Δ</div>
        <div className="absolute bottom-64 right-1/4 text-white text-6xl font-light">α</div>
        <div className="absolute top-96 left-1/2 text-white text-8xl font-light">β</div>
        <div className="absolute top-1/4 left-1/5 text-white text-7xl font-light">θ</div>
        <div className="absolute bottom-1/4 right-1/6 text-white text-6xl font-light">γ</div>
        <div className="absolute top-3/4 left-1/6 text-white text-7xl font-light">λ</div>
        <div className="absolute bottom-1/3 left-2/3 text-white text-8xl font-light">Ω</div>
        
        <div className="absolute top-1/4 left-1/3 text-white text-4xl font-light">x² + y² = r²</div>
        <div className="absolute bottom-1/4 right-1/4 text-white text-4xl font-light">E = mc²</div>
        <div className="absolute top-2/3 left-1/5 text-white text-3xl font-light">f(x) = ax + b</div>
        <div className="absolute top-1/3 right-1/5 text-white text-3xl font-light">a² + b² = c²</div>
        <div className="absolute bottom-1/2 left-1/2 text-white text-3xl font-light">y = mx + b</div>
        <div className="absolute top-1/2 left-1/4 text-white text-3xl font-light">∫ f(x)dx</div>
        <div className="absolute bottom-2/3 right-1/3 text-white text-3xl font-light">lim x→∞</div>
        
        <div className="absolute top-1/3 right-1/5 text-white text-6xl font-light">123</div>
        <div className="absolute bottom-1/3 left-1/6 text-white text-5xl font-light">456</div>
        <div className="absolute top-1/5 left-2/3 text-white text-5xl font-light">789</div>
        <div className="absolute bottom-1/5 right-1/3 text-white text-4xl font-light">2π</div>
        <div className="absolute top-2/5 left-1/2 text-white text-5xl font-light">3.14</div>
        
        <div className="absolute top-20 right-1/2 w-24 h-24 border-2 border-white rotate-45"></div>
        <div className="absolute bottom-20 left-1/2 w-16 h-16 border-2 border-white rounded-full"></div>
        <div className="absolute top-1/2 left-20 w-0 h-0 border-l-[30px] border-r-[30px] border-b-[50px] border-l-transparent border-r-transparent border-b-white"></div>
        <div className="absolute bottom-1/3 right-20 w-20 h-20 border-2 border-white rotate-12"></div>
        <div className="absolute top-3/4 right-1/4 w-12 h-12 border-2 border-white rounded-full"></div>
        
        <div className="absolute top-1/6 left-3/4 text-white text-5xl font-light">±</div>
        <div className="absolute bottom-1/6 left-1/4 text-white text-6xl font-light">×</div>
        <div className="absolute top-4/5 right-1/5 text-white text-5xl font-light">÷</div>
        <div className="absolute bottom-1/5 left-3/5 text-white text-4xl font-light">≈</div>
        <div className="absolute top-1/5 right-2/5 text-white text-5xl font-light">≠</div>
      </div>

      {/* Left Side - Branding with More Impact */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-center text-white relative z-10">
        <div className="max-w-2xl">
          <div className="mb-8 relative">
            <div className="absolute -top-4 -left-4 text-6xl opacity-30">∑</div>
            <div className="absolute -bottom-4 -right-4 text-5xl opacity-30">π</div>
            
            {/* Logo gigante e sem fundo branco, apenas com drop shadow para destaque */}
            <div className="relative z-10 mb-8">
              <img src={logoImg} alt="Logo Jogos e Matemática" className="w-[32rem] xl:w-[40rem] max-w-full h-auto drop-shadow-2xl" />
            </div>
            
            <div className="flex items-center gap-3 mb-3">
              <div className="h-1 w-16 bg-white"></div>
              <span className="text-2xl font-bold">∞</span>
              <div className="h-1 flex-1 bg-white"></div>
            </div>
            
            <p className="text-2xl text-rose-100 mb-2 font-semibold">
              Juntos somos mais fortes
            </p>
            <p className="text-xl text-rose-200">
              Transforme números em conhecimento
            </p>
          </div>
          
          <div className="mt-12 flex items-center gap-6 text-rose-200">
            <div className="text-4xl">🎲</div>
            <div className="text-4xl">📐</div>
            <div className="text-4xl">🔢</div>
            <div className="text-4xl">📊</div>
            <div className="text-4xl">🧮</div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 relative z-10">
        <div className="w-full max-w-md">
          {/* Mobile View Title Section */}
          <div className="lg:hidden mb-12 text-center text-white flex flex-col items-center">
            <img src={logoImg} alt="Logo Jogos e Matemática" className="w-80 sm:w-96 h-auto mb-6 drop-shadow-2xl" />
            <p className="text-lg text-rose-100">Faça login para continuar</p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 backdrop-blur-sm bg-opacity-95">
            <div className="mb-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-rose-600 to-red-600 rounded-full mb-3 shadow-lg relative">
                <span className="text-3xl">🔐</span>
                <span className="absolute -top-1 -right-1 text-xs opacity-60">π</span>
                <span className="absolute -bottom-1 -left-1 text-xs opacity-60">∑</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-1">Bem-vindo de volta!</h2>
              <p className="text-sm text-gray-600">Entre com suas credenciais</p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                <div className="flex items-center gap-2">
                  <span>⚠️</span>
                  <span>{error}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none transition text-sm"
                    placeholder="seu@email.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Senha
                </label>
                <div className="relative">
                  <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none transition text-sm"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white font-semibold py-2.5 px-6 rounded-lg transition duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl text-sm"
              >
                <FiLogIn />
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
            </form>

            <div className="mt-5 text-center">
              <p className="text-sm text-gray-600">
                Não tem uma conta?{' '}
                <Link to="/signup" className="text-rose-600 hover:text-rose-700 font-semibold">
                  Cadastre-se
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;